/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { isValidElement, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Handle, Connection, Position, HandleType } from 'reactflow';
import { NodeDataType } from '../../../components/Flow/Nodes';
import { useTheme } from '../../../styles/theme';
import { useDispatch, useSelector } from 'react-redux';
import { State, tabActions } from '@store/index';
import { Code } from '@common/Code';
import { Popover, alpha } from '@mui/material';
import { useNodeData } from '@common/hooks/useNodeData';
import { ArticleOutlined } from '@mui/icons-material';
import { Tooltip } from '@common/Tooltip';

interface Props {
  id: string;
  data: NodeDataType;
  anchor: {
    id: string;
    label: string;
    type: HandleType;
    variant: string;
    custom?: React.ReactNode;
  };
  isValidConnection?: (input: Connection) => boolean;
  dataDisabled?: boolean;
  isConnectable?: boolean;
}

export const AnchorGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const DialHandle = styled.div<{ position: Position; type: string; disabled: boolean; color?: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ position }) => (position === 'left' ? 'flex-start' : 'flex-end')};
  padding: 0.3rem 0;

  p {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    margin-right: ${({ type }) => (type === 'target' ? 0 : '0.6rem')};
    margin-left: ${({ type }) => (type === 'target' ? '0.6rem' : 'none')};
    color: ${({ theme, disabled, color }) => (disabled ? '#6e7681' : color || theme.text.standard)};
    font-family: 'Inter';
  }
`;

const DialHandleDisabled = styled.div<{ position: Position }>`
  display: flex;
  align-items: center;
  justify-content: ${({ position }) => (position === 'left' ? 'flex-start' : 'flex-end')};
  padding: 0.3rem 0;
  width: 100%;

  p {
    font-size: 12px;
    font-weight: 500;
    padding: 3px 6px;
    margin-right: 0.6rem;
    border: 1px dashed ${({ theme }) => theme.telnyx.red};
    border-radius: 6px;
    color: ${({ theme }) => theme.telnyx.red};
  }

  &:hover {
    cursor: pointer;
  }
`;

const Header = styled.div<{ type: string }>`
  padding: 0rem 0.2rem;
  border-radius: 5px 0 0 5px;
  border-radius: ${({ type }) => (type === 'target' ? '5px 0 0 5px' : '0 5px 5px 0')};
  background-color: ${({ theme }) => theme.telnyx.blue};
  font-size: 1.2ch;
  color: white;
`;

const createAnchor = ({
  color,
  radius,
  isConnected,
  disabled,
  height,
  width,
}: {
  color?: string;
  radius?: string;
  isConnected?: boolean;
  disabled?: boolean;
  height?: string;
  width?: string;
}) => {
  const styles = {
    backgroundColor: isConnected ? color : 'transparent',
    border: `1px solid ${color}`,
    borderColor: color,
    borderRadius: radius || '2px',
    height: height || '11px',
    width: width || '11px',
  };

  if (disabled) {
    styles.backgroundColor = 'transparent';
    styles.borderColor = '#6e7681';
  }

  return styles;
};

// const checkDisabled = (anchor: AnchorType, data: NodeDataType) => {
const checkDisabled = () => {
  return false;
  // if (data.sources.length === 0 && data.targets.length === 0) return false;

  // for (const source of data.sources) {
  //   if (source?.targetHandle !== anchor.id && anchor.variant === 'command') return true;
  // }

  // for (const target of data.targets) {
  //   if (target?.sourceHandle !== anchor.id && anchor.variant === 'output') return true;
  // }

  // return false;
};

const DataNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 8px 10px 16px;

  border-bottom: 1px solid ${({ theme }) => theme.line.light};

  p {
    font-size: 11px;
    color: ${({ theme }) => theme.text.subtext};
  }

  svg {
    font-size: 1.5ch;
    color: ${({ theme }) => theme.text.standard};
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tip {
    color: ${({ theme }) => theme.telnyx.orange};
    font-weight: bold;
  }
`;

const DataPrefix = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.telnyx.blue};
  background-color: ${({ theme }) => alpha(theme.telnyx.blue, 0.12)};
  font-size: 12px;
  border: 1px solid ${({ theme }) => alpha(theme.telnyx.blue, 0.2)};
  border-radius: 5px;
  padding: 1px 6px;
  gap: 4px;

  svg {
    font-size: 12px;
  }

  #icon {
    margin-top: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    cursor: pointer;
  }
`;

const StyledHandle = styled(Handle)`
  flex-shrink: 0;
`;

const AnchorLabel = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
`;

export const Anchor: React.FC<Props> = ({ id, anchor, data, isValidConnection, isConnectable, dataDisabled }) => {
  const dispatch = useDispatch();

  const { sources, targets } = data;
  const { type, variant, label } = anchor;

  const { getStreamEdge, isSourceStreamEnabled, getOutputData, getInputData, isStreamDisabled, getCommand } =
    useNodeData();

  const theme = useTheme();

  const dataPrefixes = useSelector((state: State) => state.tabs.active.flow?.data_prefixes) || {};
  const prefix = dataPrefixes?.[id];

  const isCustom = isValidElement(anchor?.custom);
  const position = anchor.type === 'target' ? Position.Left : Position.Right;
  const disabled = checkDisabled();
  const streamCommand = getCommand(id) as any;
  const isResponseNode = streamCommand?.config?.command_id === 'http.response';

  const isSourceStream = isSourceStreamEnabled(id, label, streamCommand);
  const streamEnabled = streamCommand?.state?.response_method === 'stream' && !isStreamDisabled(id);

  // const disabled = checkDisabled(anchor, data);

  const connecting = useSelector((state: State) => state.tabs.active.connecting);

  const isConnected =
    sources.some((connection) => connection.target === id && connection.targetHandle === anchor.id) ||
    targets.some((connection) => connection.source === id && connection.sourceHandle === anchor.id);

  const getAnchorColor = () => {
    if (variant === 'plugin') return theme.telnyx.orange;
    if (variant === 'data' && type === 'source' && isSourceStream) return '#00E3AA';
    if (variant === 'data' && type === 'target' && streamEnabled) return '#00E3AA';
    if (variant === 'data' && dataDisabled) return alpha(theme.telnyx.red, 0.4);
    if (variant === 'data') return theme.telnyx.blue;
    if (variant === 'condition') return theme.telnyx.red;
    if (type === 'source' && variant === 'action') return theme.text.standard;
    return theme.text.standard;
  };

  const getAnchorRadius = () => {
    if (variant === 'data' || variant === 'plugin' || variant === 'condition') return '1rem';
    if (type === 'source' && variant === 'action') return '2px';
    return '2px';
  };

  const style = createAnchor({
    color: getAnchorColor(),
    radius: getAnchorRadius(),
    isConnected,
    disabled,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const data = '#3434EF';
    const stream = '#00E3AA';
    const streamEdge = getStreamEdge(id);

    if (!streamEdge) return;

    if (isResponseNode) {
      if (streamEnabled) dispatch(tabActions.setEdgeStyle({ connection: streamEdge, field: 'stroke', value: stream }));
      else dispatch(tabActions.setEdgeStyle({ connection: streamEdge, field: 'stroke', value: data }));
    }

    if (type === 'source') {
      if (isSourceStream) dispatch(tabActions.setEdgeStyle({ connection: streamEdge, field: 'stroke', value: stream }));
      else dispatch(tabActions.setEdgeStyle({ connection: streamEdge, field: 'stroke', value: data }));
    }
  }, [streamEnabled, isSourceStream, isResponseNode]);

  const open = Boolean(anchorEl);

  if (anchor?.variant === 'data' && dataDisabled) {
    return (
      <Tooltip
        title='You must run this node before the data anchor becomes available.'
        placement='top'
        arrow
        sx={{ maxWidth: '200px' }}
      >
        <DialHandleDisabled position={position}>
          <p id='unknown'>Run Required</p>
          <StyledHandle
            type={type}
            style={style}
            id={anchor.id}
            position={position}
            isConnectable={false}
            isValidConnection={() => false}
          />
        </DialHandleDisabled>
      </Tooltip>
    );
  }

  if (anchor?.variant === 'data' && prefix && type === 'source') {
    return (
      <div style={{ marginTop: '2px ' }}>
        <DialHandle position={position} type={type} disabled={disabled}>
          <Tooltip
            arrow
            sx={{ maxWidth: '200px' }}
            placement='top'
            title='The data for this node is globally accessible to all other nodes in this workflow via this prefix name.'
          >
            <DataPrefix>
              <div id='icon'>
                <ArticleOutlined />
              </div>
              {prefix}
            </DataPrefix>
          </Tooltip>
        </DialHandle>
      </div>
    );
  }

  if (anchor?.variant === 'data') {
    const code: any = anchor?.type === 'source' ? getOutputData(id, anchor, true) : getInputData(id, true);
    const sourceLabel = isSourceStream && label === 'Data' ? 'Stream' : label;
    const targetLabel = isResponseNode && streamEnabled && label === 'Data' ? 'Stream' : label;

    return (
      <DialHandle position={position} type={type} disabled={disabled}>
        {!isCustom && type === 'source' && label && <AnchorLabel>{sourceLabel}</AnchorLabel>}
        {isCustom && anchor.custom}
        <StyledHandle
          type={type}
          style={style}
          id={anchor.id}
          position={position}
          isConnectable={isConnectable}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          isValidConnection={isValidConnection}
        />
        <Popover
          id='mouse-over-popover'
          sx={{
            pointerEvents: 'none',
          }}
          open={open && !connecting}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: anchor.type === 'target' ? 'right' : 'left',
          }}
          PaperProps={{
            sx: {
              width: 'auto',
              minWidth: 200,
              maxWidth: 400,
              backgroundColor: theme.background.standard,
              borderRadius: '5px',
            },
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: anchor.type === 'target' ? 'left' : 'right',
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <div style={{ display: 'flex', maxHeight: '800px' }}>
            {anchor.type === 'target' && <Header type={anchor.type} />}
            <div style={{ maxWidth: '400px', overflow: 'hidden' }}>
              <DataNotice>
                <p className='tip'>TIP:</p>
                <p>View full data in</p>
                <div className='icon'>
                  <ArticleOutlined />
                </div>
                <p>tab.</p>
              </DataNotice>
              <Code language='json' code={code ? code : 'null'} />
            </div>
            {anchor.type === 'source' && <Header type={anchor.type} />}
          </div>
        </Popover>
        {type === 'target' && label && <AnchorLabel>{targetLabel}</AnchorLabel>}
      </DialHandle>
    );
  }

  return (
    <DialHandle position={position} type={type} disabled={disabled}>
      {!isCustom && type === 'source' && label && <AnchorLabel>{label}</AnchorLabel>}
      {isCustom && anchor.custom}
      <StyledHandle
        type={type}
        style={style}
        id={anchor.id}
        position={position}
        isConnectable={isConnectable}
        isValidConnection={isValidConnection}
      />
      {type === 'target' && label && <AnchorLabel>{label}</AnchorLabel>}
    </DialHandle>
  );
};
